/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { createEnum, EnumSet, identity } from '@gh/core-data';
import { ContactType, JobCMListFilterMapper, JobListFilterMapper, LeadListFilterMapper } from '@gh/builder-shared-data';

export const FilterNames = createEnum('FILTER_NAMES', {
  jobs: 'jobs',
  jobFinancialsList: 'jobFinancialsList',
  jobCoList: 'jobCoList',
  jobOrdersList: 'jobOrdersList',
  jobSpecList: 'jobSpecList',
  jobCsList: 'jobCsList',
  jobEstimatesList: 'jobEstimatesList',
  drawList: 'drawList',
  hlpList: 'hlpList',
  hlpListCorp: 'hlpListCorp',
  hlpListMaster: 'hlpListMaster',
  priceBooks: 'priceBooks',
  contacts: 'contacts',
  contactsMaster: 'contactsMaster',
  contactsCorp: 'contactsCorp',
  salesSchedule: 'salesSchedule',
  preconstructionSchedule: 'preconstructionSchedule',
  constructionSchedule: 'constructionSchedule',
  maintenanceSchedule: 'maintenanceSchedule',
  cmList: 'cmList',
  cmListMaster: 'cmListMaster',
  quotationList: 'quotationList',
  housePlans: 'housePlans',
  leads: 'leads',
  leadsBord: 'leadsBord',
  ganttConstruction : 'ganttConstruction',
  ganttPreConstruction : 'ganttPreConstruction',
  ganttMaintenance : 'ganttMaintenance',
  ganttSales : 'ganttSales',
  ganttTemplates : 'ganttTemplates',
  ganttCombined : 'ganttCombined',
  leadsListCorp: 'leadsListCorp',
  leadsListMaster: 'leadsListMaster',
});

export type FilterNames = EnumSet<typeof FilterNames>;

export const FILTER_MAPPERS = {
  [FilterNames.jobs]: JobListFilterMapper,
  [FilterNames.jobFinancialsList]: identity(),
  [FilterNames.jobCoList]: identity(),
  [FilterNames.jobOrdersList]: identity(),
  [FilterNames.jobSpecList]: identity(),
  [FilterNames.jobCsList]: identity(),
  [FilterNames.jobEstimatesList]: identity(),
  [FilterNames.drawList]: identity(),
  [FilterNames.hlpList]: identity(),
  [FilterNames.hlpListCorp]: identity(),
  [FilterNames.hlpListMaster]: identity(),
  [FilterNames.priceBooks]: identity(),
  [FilterNames.contacts]: identity(),
  [FilterNames.contactsMaster]: identity(),
  [FilterNames.contactsCorp]: identity(),
  [FilterNames.salesSchedule]: identity(),
  [FilterNames.preconstructionSchedule]: identity(),
  [FilterNames.constructionSchedule]: identity(),
  [FilterNames.maintenanceSchedule]: identity(),
  [FilterNames.cmList]: JobCMListFilterMapper,
  [FilterNames.cmListMaster]: JobCMListFilterMapper,
  [FilterNames.quotationList]: identity(),
  [FilterNames.housePlans]: identity(),
  [FilterNames.leads]: LeadListFilterMapper,
  [FilterNames.leadsBord]: LeadListFilterMapper,
  [FilterNames.leadsListCorp]: LeadListFilterMapper,
  [FilterNames.leadsListMaster]: LeadListFilterMapper,
};

export const DEFAULT_FILTER_STATE =  {
  [FilterNames.jobs]: {
    showFinalized: false,
    showDpp: true,
    showUserAssignedJobs: false,
    showActive: true,
    showNotProceeding: false,
    isSlabExist: null,
    dateType: null,
    dateFrom: '',
    dateTo: '',
    filterType: null,
  },
  [FilterNames.jobFinancialsList]: {
    showFinalized: false,
    showDpp: true,
    showActive: true,
    showUserAssignedJobs: false,
    showNotProceeding: false,
  },
  [FilterNames.jobCoList]: {
    showFinalized: false,
    showActive: true,
    showUserAssignedJobs: false,
    showNotProceeding: false,
  },
  [FilterNames.jobOrdersList]: {
    showFinalized: false,
    showActive: true,
    showDpp: true,
    showUserAssignedJobs: false,
    showNotProceeding: false,
  },
  [FilterNames.jobSpecList]: {
    showFinalized: false,
    showActive: true,
    showDpp: true,
    showUserAssignedJobs: false,
    showNotProceeding: false,
  },
  [FilterNames.jobCsList]: {
    showFinalized: false,
    showActive: true,
    showDpp: true,
    showUserAssignedJobs: false,
    showNotProceeding: false,
  },
  [FilterNames.jobEstimatesList]: {
    showFinalized: false,
    showActive: true,
    showDpp: true,
    showNotProceeding: false,
  },
  [FilterNames.drawList]: {
    showFinalized: false,
    showActive: true,
    showNotProceeding: false,
  },
  [FilterNames.hlpList]: {
    showPublished: true,
    showDraft: false,
    showDeleted: false,
  },
  [FilterNames.hlpListCorp]: {
    showPublished: true,
    showDraft: false,
    showDeleted: false,
    salesPersonId: null,
    officeId: '',
    masterZoneId: '',
  },
  [FilterNames.hlpListMaster]: {
    showPublished: true,
    showDraft: false,
    showDeleted: false,
  },
  [FilterNames.priceBooks]: {
    showActive: true,
    showWithdrawn: false,
  },
  [FilterNames.contacts]: {
    types: [{
      type: ContactType.Other,
      checked: true,
    }, {
      type: ContactType.FinanceCompany,
      checked: true,
    }, {
      type: ContactType.Employee,
      checked: true,
    }, {
      type: ContactType.Debtor,
      checked: true,
    }, {
      type: ContactType.CityCounty,
      checked: true,
    }, {
      type: ContactType.Customer,
      checked: true,
    }, {
      type: ContactType.CreditorSupplier,
      checked: true,
    }],
    showInactive: false,
  },
  [FilterNames.contactsMaster]: {
    types: [{
      type: ContactType.Other,
      checked: true,
    }, {
      type: ContactType.FinanceCompany,
      checked: true,
    }, {
      type: ContactType.Employee,
      checked: true,
    }, {
      type: ContactType.Debtor,
      checked: true,
    }, {
      type: ContactType.CityCounty,
      checked: true,
    }, {
      type: ContactType.Customer,
      checked: true,
    }, {
      type: ContactType.CreditorSupplier,
      checked: true,
    }],
    showInactive: false,
  },
  [FilterNames.contactsCorp]: {
    types: [{
      type: ContactType.Other,
      checked: true,
    }, {
      type: ContactType.FinanceCompany,
      checked: true,
    }, {
      type: ContactType.Employee,
      checked: true,
    }, {
      type: ContactType.Debtor,
      checked: true,
    }, {
      type: ContactType.CityCounty,
      checked: true,
    }, {
      type: ContactType.Customer,
      checked: true,
    }, {
      type: ContactType.CreditorSupplier,
      checked: true,
    }],
    showInactive: false,
  },
  [FilterNames.salesSchedule]: {
    showWillNotCompleted: false,
    showCompleted: false,
    showActive: true,
  },
  [FilterNames.preconstructionSchedule]: {
    showAuthToCommence: false,
    showFinalized: false,
    showNotProceeding: false,
    showActive: true,
    showDpp: true,
  },
  [FilterNames.constructionSchedule]: {
    showFinalized: false,
    showHandover: false,
    showActive: true,
  },
  [FilterNames.maintenanceSchedule]: {
    showFinalized: true,
    showHandover: true,
    showCompleted: true,
    showActive: true,
  },
  [FilterNames.cmList]: {
    showActive: true,
    showDpp: true,
    showNotProceeding: false,
    showFinalized: false,
    officeId: '',
    masterZoneId: '',
  },
  [FilterNames.cmListMaster]: {
    showActive: true,
    showDpp: true,
    showNotProceeding: false,
    showFinalized: false,
    officeId: '',
  },
  [FilterNames.quotationList]: {
    showActive: true,
    showInactive: false,
    showCompleted: false,
    showApprovedOnly: false,
  },
  [FilterNames.housePlans]: {
    showUnusedOnly: false,
    showDeleted: false,
    salesEstimate: false,
    quickQuote: false,
  },
  [FilterNames.leads]: {
    showActive: true,
    showInactive: false,
    showCompleted: false,
    showOverdue: false,
    showSuppressed: false,
    stage: '',
    priority: '',
    referredBy: '',
  },
  [FilterNames.leadsBord]: {
    showActive: true,
    showInactive: false,
    showCompleted: false,
    showOverdue: false,
    showSuppressed: false,
  },
  [FilterNames.leadsListCorp]: {
    showActive: true,
    showInactive: false,
    showCompleted: false,
    showOverdue: false,
    showSuppressed: false,
    stage: '',
    priority: '',
    referredBy: '',
    masterZoneId: '',
    officeId: '',
    dateType: null,
    fromDate: null,
    toDate: null,
  },
  [FilterNames.leadsListMaster]: {
    showActive: true,
    showInactive: false,
    showCompleted: false,
    showOverdue: false,
    showSuppressed: false,
    stage: '',
    priority: '',
    referredBy: '',
    officeId: '',
    dateType: null,
    fromDate: null,
    toDate: null,
  },
};
